import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { extractError } from "@/api/client";
import {
  ArrowRight, Store, UtensilsCrossed, Users, Package, TrendingUp, Phone,
  MapPin, Clock, Wallet, ListOrdered, BadgeCheck, Download, RefreshCw,
} from "lucide-react";

const currency = (n) => `${Number(n || 0).toFixed(2)} د.ل`;
const dt = (iso) => (iso ? new Date(iso).toLocaleString("ar") : "—");
const statusColor = (s) => ({
  delivered: "bg-green-100 text-green-700",
  picked_up: "bg-blue-100 text-blue-700",
  assigned: "bg-yellow-100 text-yellow-700",
  pending: "bg-orange-100 text-orange-700",
  cancelled: "bg-red-100 text-red-700",
  preparing: "bg-purple-100 text-purple-700",
}[s?.toLowerCase()] || "bg-gray-100 text-gray-700");

export default function ShopProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get(`/admin/shops/${id}/profile`);
      setData(data);
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const downloadJSON = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${data.info?.slug || id}-profile.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const printProfile = () => window.print();

  if (busy && !data) return <div className="min-h-screen p-6 text-center text-[var(--text-secondary)]">جاري تحميل الملف...</div>;
  if (err) return (
    <div className="min-h-screen p-6 text-center">
      <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl inline-block">{err}</div>
      <div className="mt-4"><button onClick={load} className="text-primary-brand font-bold">إعادة المحاولة</button></div>
    </div>
  );
  if (!data) return null;

  const { info, menu_summary: menu, owners, orders_summary: orders, top_items, latest_orders } = data;

  return (
    <div className="min-h-screen bg-[var(--background)] pb-16" data-testid="shop-profile-page">
      {/* Header */}
      <header className="bg-white border-b border-[var(--border)] sticky top-0 z-10 print:static">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => nav(-1)} className="p-2 rounded-lg hover:bg-[var(--surface-alt)] print:hidden" data-testid="back-btn">
            <ArrowRight size={20}/>
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Store size={20} className="text-primary-brand"/>
              <h1 className="text-xl font-black truncate" style={{fontFamily:'Cairo'}} data-testid="shop-name">{info.name}</h1>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${info.active !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                {info.active !== false ? "نشط" : "مغلق"}
              </span>
            </div>
            <div className="text-xs text-[var(--text-secondary)] flex items-center gap-3 mt-1 flex-wrap">
              <span>{info.shop_type || "restaurant"}</span>
              <span>· ID: <code className="font-mono">{info.id?.slice(-8)}</code></span>
              {info.slug && <Link to={`/r/${info.slug}`} className="text-primary-brand hover:underline">/r/{info.slug}</Link>}
            </div>
          </div>
          <button onClick={load} className="p-2 rounded-lg hover:bg-[var(--surface-alt)] print:hidden" title="تحديث" data-testid="refresh-btn">
            <RefreshCw size={16} className={busy ? "animate-spin" : ""}/>
          </button>
          <button onClick={printProfile} className="p-2 rounded-lg hover:bg-[var(--surface-alt)] print:hidden" title="طباعة PDF" data-testid="print-btn">
            <Download size={16}/>
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 space-y-4">
        {/* Info card */}
        <section className="bg-white rounded-2xl p-5 talabya-shadow-soft" data-testid="shop-info-section">
          <h2 className="text-sm font-black mb-3 flex items-center gap-2"><BadgeCheck size={16}/> بيانات المحل</h2>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            {info.phone && <div className="flex items-center gap-2"><Phone size={14} className="text-primary-brand"/> <a href={`tel:${info.phone}`} className="font-mono hover:underline">{info.phone}</a></div>}
            {info.address && <div className="flex items-center gap-2"><MapPin size={14} className="text-primary-brand"/> <span>{info.address}</span></div>}
            {info.hours && <div className="flex items-center gap-2"><Clock size={14} className="text-primary-brand"/> <span>{info.hours}</span></div>}
            {info.commission_rate != null && <div>عمولة المنصة: <b>{Number(info.commission_rate || 0)}%</b></div>}
            {info.delivery_pricing && (
              <div className="sm:col-span-2 bg-[var(--surface-alt)] rounded-xl p-3 text-xs">
                <b>تسعير التوصيل:</b> رسوم أساسية {info.delivery_pricing.base_fee} د.ل لأول {info.delivery_pricing.base_km} كم، ثم {info.delivery_pricing.per_km} د.ل/كم · عمولة {info.delivery_pricing.platform_commission || 0} د.ل
              </div>
            )}
          </div>
        </section>

        {/* Stat cards */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="shop-stats-section">
          <StatCard icon={<ListOrdered size={16}/>} label="إجمالي الطلبات" value={orders.total} testid="stat-total-orders"/>
          <StatCard icon={<Package size={16}/>} label="آخر 30 يوم" value={orders.last30_count} testid="stat-last30"/>
          <StatCard icon={<TrendingUp size={16}/>} label="إيراد كلي" value={currency(orders.revenue_lifetime)} testid="stat-revenue-lifetime"/>
          <StatCard icon={<Wallet size={16}/>} label="إيراد آخر 30 يوم" value={currency(orders.revenue_last30)} testid="stat-revenue-30"/>
        </section>

        {/* Menu summary */}
        <section className="bg-white rounded-2xl p-5 talabya-shadow-soft" data-testid="shop-menu-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-black flex items-center gap-2"><UtensilsCrossed size={16}/> المنيو ({menu.total_items} صنف)</h2>
            <Link to={`/admin/menu?restaurant=${info.id}`} className="text-xs font-bold text-primary-brand hover:underline">تحرير المنيو ←</Link>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3 text-xs">
            <StatCard mini label="متاح الآن" value={menu.available} tone="green"/>
            <StatCard mini label="غير متاح" value={menu.unavailable} tone="red"/>
            <StatCard mini label="إجمالي الإضافات" value={menu.extras_count} tone="blue"/>
          </div>
          {menu.categories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {menu.categories.map((c) => (
                <span key={c.name} className="px-3 py-1 rounded-full bg-[var(--surface-alt)] text-xs" data-testid={`cat-${c.name}`}>
                  {c.name} <b className="text-primary-brand">{c.count}</b>
                </span>
              ))}
            </div>
          )}
        </section>

        {/* Owners */}
        <section className="bg-white rounded-2xl p-5 talabya-shadow-soft" data-testid="shop-owners-section">
          <h2 className="text-sm font-black mb-3 flex items-center gap-2"><Users size={16}/> المستخدمون المرتبطون ({owners.length})</h2>
          {owners.length === 0 ? (
            <div className="text-xs text-[var(--text-secondary)]">لا يوجد مستخدمون مربوطون بعد</div>
          ) : (
            <ul className="divide-y divide-[var(--border)]">
              {owners.map((o) => (
                <li key={o.id} className="py-2 flex items-center justify-between text-sm" data-testid={`owner-${o.id}`}>
                  <div>
                    <div className="font-bold">{o.name || "—"}</div>
                    <div className="text-xs text-[var(--text-secondary)] flex items-center gap-2">
                      {o.phone && <a href={`tel:${o.phone}`} className="hover:underline">📞 {o.phone}</a>}
                      {o.email && <span>· {o.email}</span>}
                    </div>
                  </div>
                  {o.permissions?.length > 0 && (
                    <div className="text-[10px] text-[var(--text-secondary)] text-left">
                      {o.permissions.join(", ")}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Top selling items */}
        {top_items.length > 0 && (
          <section className="bg-white rounded-2xl p-5 talabya-shadow-soft" data-testid="shop-top-items">
            <h2 className="text-sm font-black mb-3 flex items-center gap-2"><TrendingUp size={16}/> الأصناف الأكثر مبيعاً</h2>
            <ol className="space-y-2">
              {top_items.map((it, i) => (
                <li key={it.name} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-primary-light text-primary-brand text-[11px] font-black flex items-center justify-center">{i + 1}</span>
                    {it.name}
                  </span>
                  <b className="text-primary-brand">{it.sold}×</b>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Latest orders */}
        <section className="bg-white rounded-2xl p-5 talabya-shadow-soft" data-testid="shop-latest-orders">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-black flex items-center gap-2"><Package size={16}/> آخر 10 طلبات</h2>
            <Link to={`/admin/orders?restaurant=${info.id}`} className="text-xs font-bold text-primary-brand hover:underline">كل الطلبات ←</Link>
          </div>
          {latest_orders.length === 0 ? (
            <div className="text-xs text-[var(--text-secondary)]">لا توجد طلبات بعد</div>
          ) : (
            <ul className="divide-y divide-[var(--border)]">
              {latest_orders.map((o) => (
                <li key={o.id} className="py-2 flex items-center justify-between text-sm" data-testid={`order-${o.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs">{o.code}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusColor(o.status)}`}>{o.status}</span>
                    </div>
                    <div className="text-[11px] text-[var(--text-secondary)] mt-0.5 truncate">
                      {o.customer_name} · {o.customer_phone} · {o.items_count} صنف · {dt(o.created_at)}
                    </div>
                  </div>
                  <b className="shrink-0 text-primary-brand">{currency(o.total)}</b>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Download */}
        <div className="text-center">
          <button onClick={downloadJSON} className="text-xs text-primary-brand hover:underline flex items-center gap-1 mx-auto" data-testid="download-json">
            <Download size={12}/> تحميل الملف كامل (JSON)
          </button>
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, tone, mini, testid }) {
  const toneCls = {
    green: "bg-green-50 text-green-700",
    red: "bg-red-50 text-red-700",
    blue: "bg-blue-50 text-blue-700",
  }[tone] || "bg-primary-light text-primary-brand";
  return (
    <div className={`bg-white rounded-2xl ${mini ? "p-3" : "p-4"} talabya-shadow-soft`} data-testid={testid}>
      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold ${toneCls}`}>
        {icon}{label}
      </div>
      <div className={`font-black mt-2 ${mini ? "text-base" : "text-lg"}`} style={{fontFamily:'Cairo'}}>{value}</div>
    </div>
  );
}
